import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { pathCommandsForSides } from '@/lib/elements/shape-path-commands'
import {
  SIDES_ELEMENT_KIND_ID,
  innerRatioFromInsetPercent,
  isConvertedToRegularPolygon,
  isNoOpInsetChange,
  isNoOpSidesChange,
  normInset,
  normSides,
  sidesChangeConvertsToRegularPolygon,
} from '@/lib/object-effects/sides'

/**
 * Live updates and undo before/after for the Sides and Inset effects. Both regenerate the shape's
 * path and stamp it as a kindId-10 regular polygon (`SIDES_ELEMENT_KIND_ID`); undo has to restore
 * the kind the shape had before the edit.
 */

type HistoryPair = { before: Partial<ShapeData>; after: Partial<ShapeData> }

/**
 * Restore the pre-edit kind, or undoing a rectangle's first conversion would leave it a kindId-10
 * polygon — and a later "set 4" (Sides) or "clear inset" (Inset) would then draw a diamond.
 */
function restoredKindId(startKindId: number | undefined, sh: ShapeData): number {
  return startKindId ?? (sh.type === 'square' ? 1 : SIDES_ELEMENT_KIND_ID)
}

export function buildSidesLiveUpdate(cur: ShapeData, v: number): Partial<ShapeData> | null {
  // Setting a rectangle/square to the 4 sides it already has must change nothing — without
  // this it would still get restamped as a kindId-10 polygon carrying a rectangle path.
  if (isNoOpSidesChange(cur, v)) return null

  const inset = normInset(cur)
  const cmds = pathCommandsForSides(
    cur.type,
    v,
    cur.width,
    cur.height,
    innerRatioFromInsetPercent(inset),
    { asRegularPolygon: sidesChangeConvertsToRegularPolygon(cur, v) }
  )

  return {
    sides: v,
    inset,
    pathCommands: cmds,
    kindId: SIDES_ELEMENT_KIND_ID,
  }
}

/**
 * Callers skip shapes whose `start === v`. That covers the rectangle no-op too: a fresh square
 * asked for the 4 sides it already has has `start === v`, so it never reaches the document. Do not
 * re-test `isNoOpSidesChange(sh, v)` here — the live apply has already run, so `sh` carries the
 * post-edit values.
 */
export function buildSidesHistoryItem(
  sh: ShapeData,
  start: number,
  v: number,
  startKindId: number | undefined
): HistoryPair {
  const w = sh.width
  const h = sh.height
  const inset = normInset(sh)
  // The kind from *before* the live apply (which stamps kindId 10 on anything it converts)
  // decides how `start` is drawn: a rectangle that had never been converted reverts to its
  // axis-aligned path, an established polygon back to its 4-gon diamond.
  const startedAsRegularPolygon = startKindId === SIDES_ELEMENT_KIND_ID
  const beforeCmds = pathCommandsForSides(sh.type, start, w, h, innerRatioFromInsetPercent(inset), {
    asRegularPolygon: startedAsRegularPolygon,
  })
  const afterCmds = pathCommandsForSides(sh.type, v, w, h, innerRatioFromInsetPercent(inset), {
    asRegularPolygon: sidesChangeConvertsToRegularPolygon(
      { ...sh, kindId: startKindId, sides: start },
      v
    ),
  })

  return {
    before: {
      sides: start,
      inset,
      pathCommands: beforeCmds,
      kindId: restoredKindId(startKindId, sh),
    },
    after: {
      sides: v,
      inset,
      pathCommands: afterCmds,
      kindId: SIDES_ELEMENT_KIND_ID,
    },
  }
}

export function buildInsetLiveUpdate(cur: ShapeData, v: number): Partial<ShapeData> | null {
  // Setting a rectangle/square's inset back to the 0 it already has must change nothing —
  // without this it would still get restamped as a kindId-10 polygon carrying a diamond path.
  if (isNoOpInsetChange(cur, v)) return null

  const sides = normSides(cur)
  const cmds = pathCommandsForSides(
    cur.type,
    sides,
    cur.width,
    cur.height,
    innerRatioFromInsetPercent(v),
    // An already-converted 4-sided square is a diamond; clearing its inset back to 0 must
    // keep it one rather than snapping to an axis-aligned rectangle.
    { asRegularPolygon: isConvertedToRegularPolygon(cur) }
  )

  return {
    inset: v,
    sides,
    pathCommands: cmds,
    kindId: SIDES_ELEMENT_KIND_ID,
  }
}

export function buildInsetHistoryItem(
  sh: ShapeData,
  start: number,
  v: number,
  startKindId: number | undefined
): HistoryPair {
  const sides = normSides(sh)
  // The kind from *before* the live apply (which stamps kindId 10 on anything it converts)
  // decides how `start` is drawn: a rectangle that had never been converted reverts to its
  // axis-aligned path, an established polygon back to its 4-gon diamond.
  const startedAsRegularPolygon = startKindId === SIDES_ELEMENT_KIND_ID
  const beforeCmds = pathCommandsForSides(
    sh.type,
    sides,
    sh.width,
    sh.height,
    innerRatioFromInsetPercent(start),
    { asRegularPolygon: startedAsRegularPolygon }
  )
  const afterCmds = pathCommandsForSides(
    sh.type,
    sides,
    sh.width,
    sh.height,
    innerRatioFromInsetPercent(v),
    // Not `|| v > 0`: Inset never performs the rectangle→diamond conversion, so the shape's
    // pre-edit kind is the whole answer. Unconverted rectangles cannot reach here anyway —
    // `isInsetEligibleShape` keeps them out of `eligible`.
    { asRegularPolygon: startedAsRegularPolygon }
  )

  return {
    before: {
      inset: start,
      sides,
      pathCommands: beforeCmds,
      kindId: restoredKindId(startKindId, sh),
    },
    after: {
      inset: v,
      sides,
      pathCommands: afterCmds,
      kindId: SIDES_ELEMENT_KIND_ID,
    },
  }
}
